import React, { useRef, useEffect } from 'react';

/**
 * ProfileVisualizer - 2D canvas drawing of the lathe profile contour
 * Shows stock outline, finish profile, and dimensions
 */
const ProfileVisualizer = ({ stages = [], stockDia = 2.0, cycleType = 'g71', facingParams = null, depthOfCut = 0, finishAllowX = 0 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const w = canvas.width = canvas.offsetWidth * 2; // Retina
    const h = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const cw = w / 2;
    const ch = h / 2;

    // Clear
    ctx.fillStyle = '#0f1419';
    ctx.fillRect(0, 0, cw, ch);

    if (cycleType === 'g72' && facingParams) {
      drawFacingProfile(ctx, cw, ch, facingParams, stockDia);
      return;
    }

    if (!stages.length) return;

    // Calculate bounds
    let maxDia = stockDia;
    let totalLength = 0;
    for (const s of stages) {
      maxDia = Math.max(maxDia, s.startDia, s.endDia);
      totalLength += s.length;
    }
    const maxRadius = maxDia / 2;
    const stockRadius = stockDia / 2;

    // Scale to fit with padding
    const padX = 50, padY = 40;
    const drawW = cw - padX * 2;
    const drawH = ch - padY * 2;
    const scaleZ = drawW / (totalLength * 1.2 || 1);
    const scaleR = drawH / (maxDia * 0.7 || 1);
    const scale = Math.min(scaleZ, scaleR);

    const originX = padX + 20;
    const centerY = ch / 2;

    const toScreen = (z, r) => ({
      x: originX - z * scale,
      y: centerY - r * scale
    });

    // Draw centerline
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(padX - 10, centerY);
    ctx.lineTo(cw - padX + 10, centerY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Centerline label
    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.fillText('CL', padX - 10, centerY + 12);

    // Draw stock outline (both halves)
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 3]);
    const stockTopLeft = toScreen(0, stockRadius);
    const stockTopRight = toScreen(-totalLength * 1.1, stockRadius);
    const stockBotLeft = toScreen(0, -stockRadius);
    const stockBotRight = toScreen(-totalLength * 1.1, -stockRadius);
    
    ctx.beginPath();
    ctx.moveTo(stockTopLeft.x, stockTopLeft.y);
    ctx.lineTo(stockTopRight.x, stockTopRight.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(stockBotLeft.x, stockBotLeft.y);
    ctx.lineTo(stockBotRight.x, stockBotRight.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Stock label
    ctx.fillStyle = '#475569';
    ctx.font = '9px monospace';
    const stockLabel = `Stock: ${stockDia.toFixed(3)}"`;
    ctx.fillText(stockLabel, stockTopRight.x + 5, stockTopRight.y - 4);

    // ============ ROUGHING PASSES OVERLAY (G71) ============
    if (cycleType === 'g71' && depthOfCut > 0 && stages.length > 0) {
      // Build the finish profile as an array of {z, r} points (radius, not diameter)
      const profilePts = [];
      let pzPos = 0;
      profilePts.push({ z: 0, r: stages[0].startDia / 2 });
      
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        const prevR = i > 0 ? stages[i-1].endDia / 2 : s.startDia / 2;
        
        if (i > 0 && Math.abs(prevR - s.startDia / 2) > 0.001) {
          profilePts.push({ z: pzPos, r: s.startDia / 2 });
        }
        const endZ = pzPos - s.length;
        profilePts.push({ z: endZ, r: s.endDia / 2 });
        pzPos = endZ;
      }
      
      // Function to get the maximum allowed radius at a given Z (from the finish profile + allowance)
      const finishAllowR = finishAllowX / 2; // Convert diameter allowance to radius
      const getProfileRadiusAtZ = (z) => {
        // Walk through profile segments to find the radius at this Z
        for (let i = 0; i < profilePts.length - 1; i++) {
          const p1 = profilePts[i];
          const p2 = profilePts[i + 1];
          // Check if Z is between p1 and p2 (note: Z goes negative)
          if ((z <= p1.z && z >= p2.z) || (z >= p1.z && z <= p2.z)) {
            if (Math.abs(p2.z - p1.z) < 0.0001) {
              // Vertical step (shoulder) - return the larger radius
              return Math.max(p1.r, p2.r) + finishAllowR;
            }
            // Interpolate
            const t = (z - p1.z) / (p2.z - p1.z);
            return p1.r + t * (p2.r - p1.r) + finishAllowR;
          }
        }
        return stockRadius; // Default to stock
      };

      // Calculate roughing pass radii (stepping down from stock)
      const roughPasses = [];
      let currentR = stockRadius;
      while (currentR - depthOfCut > 0) {
        currentR -= depthOfCut;
        // Only add pass if it's above the smallest profile radius + allowance
        const minProfileR = Math.min(...profilePts.map(p => p.r)) + finishAllowR;
        if (currentR > minProfileR) {
          roughPasses.push(currentR);
        } else {
          break;
        }
      }

      // Draw material removal area (filled region between stock and finish profile)
      ctx.fillStyle = 'rgba(239, 68, 68, 0.06)'; // Very subtle red fill
      ctx.beginPath();
      let sp = toScreen(0, stockRadius);
      ctx.moveTo(sp.x, sp.y);
      // Top stock line
      sp = toScreen(-totalLength * 1.05, stockRadius);
      ctx.lineTo(sp.x, sp.y);
      // Down to finish profile (reversed)
      pzPos = 0;
      const revPts = [];
      revPts.push({ z: 0, r: stages[0].startDia / 2 + finishAllowR });
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        if (i > 0 && Math.abs((stages[i-1].endDia / 2) - s.startDia / 2) > 0.001) {
          revPts.push({ z: pzPos, r: s.startDia / 2 + finishAllowR });
        }
        const endZ = pzPos - s.length;
        revPts.push({ z: endZ, r: s.endDia / 2 + finishAllowR });
        pzPos = endZ;
      }
      revPts.push({ z: pzPos, r: stockRadius }); // Back to stock at end
      // Draw the finish profile boundary (reversed for fill)
      for (let i = revPts.length - 1; i >= 0; i--) {
        sp = toScreen(revPts[i].z, revPts[i].r);
        ctx.lineTo(sp.x, sp.y);
      }
      ctx.closePath();
      ctx.fill();

      // Draw each roughing pass line
      for (let passIdx = 0; passIdx < roughPasses.length; passIdx++) {
        const passR = roughPasses[passIdx];
        const passAlpha = 0.3 + (passIdx / roughPasses.length) * 0.4; // Deeper passes brighter
        ctx.strokeStyle = `rgba(59, 130, 246, ${passAlpha})`; // Blue
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 2]);
        ctx.beginPath();

        // Start from Z=0, move left along this pass radius
        // Stop when we hit the profile boundary
        let started = false;
        const zStep = totalLength / 200; // Resolution
        
        for (let zi = 0; zi <= totalLength * 1.05; zi += zStep) {
          const z = -zi;
          const profileR = getProfileRadiusAtZ(z);
          
          if (passR > profileR) {
            // This pass is above the profile here - draw the cut
            const pt = toScreen(z, passR);
            if (!started) {
              ctx.moveTo(pt.x, pt.y);
              started = true;
            } else {
              ctx.lineTo(pt.x, pt.y);
            }
          } else if (started) {
            // Hit the profile - draw the retract
            const pt = toScreen(z, profileR);
            ctx.lineTo(pt.x, pt.y);
            break;
          }
        }
        ctx.stroke();
        ctx.setLineDash([]);

        // Pass number label (only for first and last)
        if (passIdx === 0 || passIdx === roughPasses.length - 1) {
          const labelPt = toScreen(-totalLength * 1.08, passR);
          ctx.fillStyle = `rgba(59, 130, 246, ${passAlpha + 0.2})`;
          ctx.font = '8px monospace';
          ctx.fillText(`P${passIdx + 1}`, labelPt.x - 2, labelPt.y + 3);
        }
      }

      // Draw finish allowance boundary
      if (finishAllowR > 0) {
        ctx.strokeStyle = 'rgba(34, 197, 94, 0.4)'; // Green
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        let fzPos = 0;
        let fp = toScreen(0, stages[0].startDia / 2 + finishAllowR);
        ctx.moveTo(fp.x, fp.y);
        for (let i = 0; i < stages.length; i++) {
          const s = stages[i];
          const prevR = i > 0 ? stages[i-1].endDia / 2 : s.startDia / 2;
          if (i > 0 && Math.abs(prevR - s.startDia / 2) > 0.001) {
            fp = toScreen(fzPos, s.startDia / 2 + finishAllowR);
            ctx.lineTo(fp.x, fp.y);
          }
          const endZ = fzPos - s.length;
          fp = toScreen(endZ, s.endDia / 2 + finishAllowR);
          ctx.lineTo(fp.x, fp.y);
          fzPos = endZ;
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    // ============ END ROUGHING OVERLAY ============

    // Draw finish profile (top half - the actual cutting profile)
    ctx.strokeStyle = '#f97316'; // Orange
    ctx.lineWidth = 2;
    ctx.beginPath();

    let zPos = 0;
    // Start at face, first stage diameter
    let p = toScreen(0, stages[0].startDia / 2);
    ctx.moveTo(p.x, p.y);

    for (let i = 0; i < stages.length; i++) {
      const s = stages[i];
      const prevDia = i > 0 ? stages[i - 1].endDia : s.startDia;

      // Shoulder step (if diameter changes between stages)
      if (i > 0 && Math.abs(prevDia - s.startDia) > 0.001) {
        if (s.entryR > 0) {
          // Draw radius arc approximation
          const midZ = zPos - s.entryR;
          const pts = 8;
          for (let j = 1; j <= pts; j++) {
            const t = j / pts;
            const rz = zPos - s.entryR * Math.sin(t * Math.PI / 2);
            const rr = (prevDia / 2) + ((s.startDia / 2 - prevDia / 2)) * (1 - Math.cos(t * Math.PI / 2));
            p = toScreen(rz, rr);
            ctx.lineTo(p.x, p.y);
          }
          zPos = midZ;
        } else {
          p = toScreen(zPos, s.startDia / 2);
          ctx.lineTo(p.x, p.y);
        }
      }

      // Straight or taper cut
      const endZ = zPos - s.length;
      p = toScreen(endZ, s.endDia / 2);
      ctx.lineTo(p.x, p.y);
      zPos = endZ;
    }
    ctx.stroke();

    // Draw mirror (bottom half) in dimmer color
    ctx.strokeStyle = '#f9731650';
    ctx.lineWidth = 1.5;
    ctx.beginPath();

    zPos = 0;
    p = toScreen(0, -stages[0].startDia / 2);
    ctx.moveTo(p.x, p.y);

    for (let i = 0; i < stages.length; i++) {
      const s = stages[i];
      const prevDia = i > 0 ? stages[i - 1].endDia : s.startDia;
      if (i > 0 && Math.abs(prevDia - s.startDia) > 0.001) {
        if (s.entryR > 0) {
          const pts = 8;
          for (let j = 1; j <= pts; j++) {
            const t = j / pts;
            const rz = zPos - s.entryR * Math.sin(t * Math.PI / 2);
            const rr = -(prevDia / 2) - ((s.startDia / 2 - prevDia / 2)) * (1 - Math.cos(t * Math.PI / 2));
            p = toScreen(rz, rr);
            ctx.lineTo(p.x, p.y);
          }
          zPos -= s.entryR;
        } else {
          p = toScreen(zPos, -s.startDia / 2);
          ctx.lineTo(p.x, p.y);
        }
      }
      const endZ = zPos - s.length;
      p = toScreen(endZ, -s.endDia / 2);
      ctx.lineTo(p.x, p.y);
      zPos = endZ;
    }
    ctx.stroke();

    // Draw dimension annotations
    ctx.fillStyle = '#94a3b8';
    ctx.font = '10px monospace';
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 0.5;

    // Diameter dimensions for each stage
    zPos = 0;
    for (let i = 0; i < stages.length; i++) {
      const s = stages[i];
      const midZ = zPos - s.length / 2;
      const dia = (s.startDia + s.endDia) / 2;
      const p1 = toScreen(midZ, dia / 2 + 0.05);
      
      ctx.fillStyle = '#e2e8f0';
      ctx.font = 'bold 10px monospace';
      
      if (Math.abs(s.startDia - s.endDia) < 0.001) {
        ctx.fillText(`\u00D8${s.startDia.toFixed(3)}"`, p1.x - 25, p1.y - 6);
      } else {
        ctx.fillText(`\u00D8${s.startDia.toFixed(3)}\u2192${s.endDia.toFixed(3)}"`, p1.x - 35, p1.y - 6);
      }

      // Length dimension below centerline
      const len1 = toScreen(zPos, -maxDia / 2 - 0.15);
      const len2 = toScreen(zPos - s.length, -maxDia / 2 - 0.15);
      ctx.strokeStyle = '#64748b';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(len1.x, len1.y);
      ctx.lineTo(len2.x, len2.y);
      ctx.stroke();

      ctx.fillStyle = '#94a3b8';
      ctx.font = '9px monospace';
      ctx.fillText(`${s.length.toFixed(3)}"`, (len1.x + len2.x) / 2 - 15, len1.y + 12);

      zPos -= s.length;
    }

    // Z-axis label
    ctx.fillStyle = '#475569';
    ctx.font = '10px monospace';
    ctx.fillText('Z\u2192', cw - padX - 5, centerY + 4);
    ctx.fillText('\u2190Z', padX - 10, centerY + 4);

  }, [stages, stockDia, cycleType, facingParams, depthOfCut, finishAllowX]);

  return (
    <div className="bg-industrial-bg border border-industrial-border rounded-lg overflow-hidden" data-testid="profile-visualizer">
      <div className="px-3 py-1.5 bg-industrial-surface border-b border-industrial-border flex items-center justify-between">
        <span className="text-xs text-orange-400 font-medium">Profile Preview</span>
        <div className="flex items-center gap-3 text-[10px] text-industrial-text-secondary">
          <span><span className="inline-block w-3 h-0.5 bg-orange-500 mr-1 align-middle" />Finish</span>
          {depthOfCut > 0 && <span><span className="inline-block w-3 h-0.5 bg-blue-500 mr-1 align-middle" style={{borderTop:'1px dashed'}} />Rough Passes</span>}
          {finishAllowX > 0 && <span><span className="inline-block w-3 h-0.5 bg-green-500 mr-1 align-middle" style={{borderTop:'1px dashed'}} />Allowance</span>}
          <span className="text-industrial-text-secondary/50">Dashed = stock</span>
        </div>
      </div>
      <canvas ref={canvasRef} className="w-full" style={{ height: '220px' }} />
    </div>
  );
};

function drawFacingProfile(ctx, cw, ch, params, stockDia) {
  const { finalDia, faceDepth } = params;
  const padX = 50, padY = 30;
  const stockR = stockDia / 2;
  const finalR = finalDia / 2;

  const maxDim = Math.max(stockDia, faceDepth * 2);
  const scale = Math.min((cw - padX * 2) / (faceDepth * 2.5 || 1), (ch - padY * 2) / (stockDia * 0.8 || 1));
  const originX = padX + 30;
  const centerY = ch / 2;

  const toScreen = (z, r) => ({ x: originX - z * scale, y: centerY - r * scale });

  // Centerline
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 0.5;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(padX - 10, centerY);
  ctx.lineTo(cw - padX + 10, centerY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Stock outline
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 3]);
  const sl = toScreen(0, stockR);
  const sr = toScreen(-faceDepth * 1.5, stockR);
  ctx.beginPath(); ctx.moveTo(sl.x, sl.y); ctx.lineTo(sr.x, sr.y); ctx.stroke();
  const bl = toScreen(0, -stockR);
  const br = toScreen(-faceDepth * 1.5, -stockR);
  ctx.beginPath(); ctx.moveTo(bl.x, bl.y); ctx.lineTo(br.x, br.y); ctx.stroke();
  ctx.setLineDash([]);

  // Face profile (top half)
  ctx.strokeStyle = '#f97316';
  ctx.lineWidth = 2;
  ctx.beginPath();
  let p = toScreen(-faceDepth, finalR);
  ctx.moveTo(p.x, p.y);
  p = toScreen(-faceDepth, stockR);
  ctx.lineTo(p.x, p.y);
  p = toScreen(0, stockR);
  ctx.lineTo(p.x, p.y);
  ctx.stroke();

  // Mirror
  ctx.strokeStyle = '#f9731650';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  p = toScreen(-faceDepth, -finalR);
  ctx.moveTo(p.x, p.y);
  p = toScreen(-faceDepth, -stockR);
  ctx.lineTo(p.x, p.y);
  p = toScreen(0, -stockR);
  ctx.lineTo(p.x, p.y);
  ctx.stroke();

  // Dimensions
  ctx.fillStyle = '#e2e8f0';
  ctx.font = 'bold 10px monospace';
  p = toScreen(-faceDepth / 2, stockR + 0.1);
  ctx.fillText(`Depth: ${faceDepth.toFixed(3)}"`, p.x - 20, p.y - 8);
  p = toScreen(-faceDepth - 0.05, (stockR + finalR) / 2);
  ctx.fillText(`\u00D8${finalDia.toFixed(3)}"`, p.x + 8, p.y + 4);
}

export default ProfileVisualizer;
